import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { useWhatsAppContact } from "../context/WhatsAppContactContext";
import { WHATSAPP_CONTACT, buildWhatsAppMessage, buildWhatsAppUrl } from "../config/whatsappContact";
import "../styles/WhatsAppContact.css";

const PRE_LOGIN_PATHS = ["/signin", "/error"];
const FOCUSABLE_SELECTOR = "button:not([disabled]), a[href], [tabindex]:not([tabindex=\"-1\"])";

export const WhatsAppContactModal = ({ onClose, href, returnFocusRef }) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    const previouslyFocused = returnFocusRef?.current || document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, [returnFocusRef]);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.querySelector(".whatsapp-modal__primary")?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const keepFocus = (event) => {
      if (dialog && !dialog.contains(event.target)) dialog.querySelector(FOCUSABLE_SELECTOR)?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", keepFocus);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", keepFocus);
    };
  }, [onClose]);


  return createPortal(
    <div
      className="whatsapp-modal__backdrop"
      data-testid="whatsapp-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="whatsapp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex="-1"
      >
        <header className="whatsapp-modal__header">
          <span className="whatsapp-modal__badge" aria-hidden="true"><FaWhatsapp /></span>
          <h2 id={titleId}>{t("whatsapp.title")}</h2>
          <button type="button" className="whatsapp-modal__close" aria-label={t("whatsapp.close")} onClick={onClose}>
            ×
          </button>
        </header>
        <div id={bodyId} className="whatsapp-modal__body">
          <p>{t("whatsapp.intro")}</p>
          <dl className="whatsapp-modal__facts">
            <div>
              <dt>{t("whatsapp.numberLabel")}</dt>
              <dd data-testid="whatsapp-number">{WHATSAPP_CONTACT.displayNumber}</dd>
            </div>
            <div className="whatsapp-modal__hours">
              <dt>{t("whatsapp.hoursLabel")}</dt>
              <dd data-testid="whatsapp-hours">{t("whatsapp.hours")}</dd>
            </div>
          </dl>
          <p className="whatsapp-modal__note">{t("whatsapp.outOfHours")}</p>
          <h3 className="whatsapp-modal__subtitle">{t("whatsapp.howTo")}</h3>
          <ol className="whatsapp-modal__steps">
            <li>{t("whatsapp.steps.open")}</li>
            <li>{t("whatsapp.steps.authorise")}</li>
            <li>{t("whatsapp.steps.matter")}</li>
          </ol>
        </div>
        <footer className="whatsapp-modal__footer">
          <button type="button" className="whatsapp-modal__secondary" onClick={onClose}>
            {t("whatsapp.cancel")}
          </button>
          <a
            className="whatsapp-modal__primary"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-open-link"
          >
            <FaWhatsapp aria-hidden="true" />
            {t("whatsapp.open")}
          </a>
        </footer>
      </section>
    </div>,
    document.body,
  );
};

const WhatsAppContact = () => {
  const { tokens, user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const context = useWhatsAppContact();
  const [open, setOpen] = useState(false);
  const visible = Boolean(tokens?.idToken) && !PRE_LOGIN_PATHS.includes(location.pathname);

  useEffect(() => {
    if (!visible) {
      setOpen(false);
      return undefined;
    }
    document.body.classList.add("has-whatsapp-fab");
    return () => document.body.classList.remove("has-whatsapp-fab");
  }, [visible]);

  const fabRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  if (!visible) return null;

  const message = buildWhatsAppMessage({
    template: t("whatsapp.prefill"),
    matterTemplate: t("whatsapp.prefillMatter"),
    name: user?.name || user?.fullName || user?.email,
    cocoCode: user?.cocoCode,
    matterNumber: context?.matterNumber,
  });

  return (
    <>
      <button
        type="button"
        ref={fabRef}
        className="whatsapp-fab"
        aria-label={t("whatsapp.buttonLabel")}
        title={t("whatsapp.buttonLabel")}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="whatsapp-fab"
        onClick={() => setOpen(true)}
      >
        <FaWhatsapp aria-hidden="true" />
      </button>
      {open && <WhatsAppContactModal onClose={close} href={buildWhatsAppUrl(message)} returnFocusRef={fabRef} />}
    </>
  );
};

export default WhatsAppContact;
